/** Finite, seeded range/valley systems, upstream of rivers and settlement carving.
 * Descriptors live in planet space, independent of cube faces, tiles and LOD.
 * The spatial index contains immutable references; sampling never allocates. */
import { rngFromString, hashSeed } from './rng.js';
import * as pmath from './pmath.js';
import { planarField } from './landforms.js';
import { plateauProfileSample, samplePlateauProfile } from './plateauLandforms.js';
export { plateauProfileSample, samplePlateauProfile } from './plateauLandforms.js';
export type { PlateauProfileSample, PlateauProfileSource } from './plateauLandforms.js';
import type { PlanetDef } from './universe.js';

type V3 = readonly [number, number, number];
export interface RegionalLandform {
  readonly id: string;
  readonly kind: 'range' | 'plateau';
  readonly center: V3;
  readonly along: V3;
  readonly across: V3;
  readonly halfLengthM: number;
  readonly widthM: number;
  readonly reliefM: number;
  /** Seven connected knots: asymmetric crests, saddles, and a winding floor. */
  readonly bends: readonly number[];
  readonly crests: readonly number[];
  readonly secondaryCrests: readonly number[];
  readonly floorStartM: number;
  readonly floorEndM: number;
  readonly seed: number;
}
export interface RegionalSample {
  weight: number;
  heightM: number;
  mountain: number;
  valley: number;
  foothill: number;
  plateau: number;
}
export const regionalSample = (): RegionalSample => ({weight:0,heightM:0,mountain:0,valley:0,foothill:0,plateau:0});
const smooth = (v:number):number => {const t=Math.max(0,Math.min(1,v));return t*t*(3-2*t);};
const dot = (a:V3,b:V3):number => a[0]*b[0]+a[1]*b[1]+a[2]*b[2];
const normalize = (x:number,y:number,z:number):V3 => {const l=Math.sqrt(x*x+y*y+z*z);return [x/l,y/l,z/l];};
const cross = (a:V3,b:V3):V3 => [a[1]*b[2]-a[2]*b[1],a[2]*b[0]-a[0]*b[2],a[0]*b[1]-a[1]*b[0]];

/** Stable geographic coordinates for surveys, hydrology and tests. */
export function regionalDirection(f:RegionalLandform,radius:number,u:number,v:number):V3 {
  return normalize(f.center[0]*radius+f.along[0]*u+f.across[0]*v,
    f.center[1]*radius+f.along[1]*u+f.across[1]*v,
    f.center[2]*radius+f.along[2]*u+f.across[2]*v);
}

export class RegionalLandforms {
  readonly enabled:boolean;
  readonly features:readonly RegionalLandform[];
  private readonly cells=new Map<number,readonly RegionalLandform[]>();
  private readonly cellM:number;
  private readonly gridSize:number;
  private readonly seaM:number;
  private readonly plateauProfile=plateauProfileSample();

  constructor(private readonly def:PlanetDef,continentalM:(x:number,y:number,z:number)=>number) {
    // Specialist geology retains its approved native catchments. Flat authored
    // stages explicitly opt out, as they do for local and mountain landforms.
    this.enabled=def.terrain.amplitudeRel>=.001&&def.archetype!=='ice'&&def.archetype!=='volcanic';
    this.cellM=Math.min(1800,def.radius*.13);
    this.gridSize=Math.ceil(2*def.radius/this.cellM)+3;
    this.seaM=def.seaRadius===null?-1e6:def.seaRadius-def.radius;
    const features:RegionalLandform[]=[];
    this.features=features;
    if(!this.enabled){Object.freeze(features);return;}
    const rng=rngFromString(`${def.seed}:regional-landforms:v1`),phase=rng()*pmath.TWO_PI;
    const count=144;
    for(let i=0;i<count;i++) {
      const y=1-2*(i+.5)/count,angle=phase+i*2.399963229728653,r=Math.sqrt(1-y*y);
      const center:V3=[r*pmath.cos(angle),y,r*pmath.sin(angle)];
      const random=rngFromString(`${def.seed}:regional-landforms:v1:${i}`);
      const native=continentalM(...center);
      if(native<this.seaM+32)continue;
      // Sparse finite regions leave generous ordinary plains between ranges.
      if(features.some(f=>dot(center,f.center)>.93))continue;
      const east=normalize(-center[2],0,center[0]),north=cross(center,east),yaw=random()*pmath.TWO_PI;
      const co=pmath.cos(yaw),si=pmath.sin(yaw);
      const along:V3=[east[0]*co+north[0]*si,east[1]*co+north[1]*si,east[2]*co+north[2]*si];
      const across=cross(center,along);
      const halfLengthM=Math.min(4200,def.radius*(.19+random()*.085));
      const widthM=Math.min(1100,def.radius*(.043+random()*.017));
      const reliefM=Math.min(1100,def.radius*def.terrain.amplitudeRel*(.90+random()*.75));
      const bends:number[]=[],crests:number[]=[],secondaryCrests:number[]=[];
      for(let k=0;k<7;k++) {
        bends.push((random()-.5)*widthM*1.1);
        // Alternating broad summits and lower saddles give each chain a clear
        // skyline. The two flanks have different heights and peak positions.
        crests.push(k%2===1?.88+random()*.36:.40+random()*.24);
        secondaryCrests.push(k%2===0?.58+random()*.30:.28+random()*.24);
      }
      const draft:RegionalLandform={id:`${def.id}:range:${i}`,kind:random()<.22?'plateau':'range',center,along,across,
        halfLengthM,widthM,reliefM,bends,crests,secondaryCrests,floorStartM:0,floorEndM:0,seed:hashSeed(`${def.seed}:range-rock:${i}`)};
      const a=continentalM(...regionalDirection(draft,def.radius,-halfLengthM,0));
      const b=continentalM(...regionalDirection(draft,def.radius,halfLengthM,0));
      // A gentle longitudinal grade gives the low corridor an outlet direction.
      // No artificial inland water plane is introduced by this dry landform.
      const middle=Math.max(this.seaM+30,Math.min(native,(a+b)*.5));
      const delta=Math.max(-halfLengthM*.10,Math.min(halfLengthM*.10,(b-a)*.5));
      const floorStartM=Math.max(this.seaM+24,middle-delta),floorEndM=Math.max(this.seaM+24,middle+delta);
      features.push(Object.freeze({...draft,floorStartM,floorEndM,center:Object.freeze(center),along:Object.freeze(along),across:Object.freeze(across),
        bends:Object.freeze(bends),crests:Object.freeze(crests),secondaryCrests:Object.freeze(secondaryCrests)}));
    }
    Object.freeze(features);
    // Conservative 3D bounds cover the entire spherical projected footprint,
    // including the radial drop from the tangent plane. No face-edge seam.
    const mutable=new Map<number,RegionalLandform[]>(),R=def.radius;
    for(const f of features) {
      const reach=Math.sqrt(f.halfLengthM*f.halfLengthM+16*f.widthM*f.widthM);
      const bound=reach+R*(1-R/Math.sqrt(R*R+reach*reach));
      const lo=f.center.map(v=>Math.max(0,Math.floor((v*R-bound+R)/this.cellM)));
      const hi=f.center.map(v=>Math.min(this.gridSize-1,Math.floor((v*R+bound+R)/this.cellM)));
      for(let x=lo[0];x<=hi[0];x++)for(let y=lo[1];y<=hi[1];y++)for(let z=lo[2];z<=hi[2];z++) {
        const key=this.key(x,y,z);let list=mutable.get(key);if(!list)mutable.set(key,list=[]);list.push(f);
      }
    }
    for(const [key,list] of mutable)this.cells.set(key,Object.freeze(list));
  }
  private key(x:number,y:number,z:number):number {return (x*this.gridSize+y)*this.gridSize+z;}

  /** Absolute macro height + normalized habitat intent. Blends overlapping
   * descriptors with bounded weights; neither order nor cache warmth matters. */
  sample(dx:number,dy:number,dz:number,nativeM:number,out:RegionalSample):RegionalSample {
    out.weight=out.heightM=out.mountain=out.valley=out.foothill=out.plateau=0;
    if(!this.enabled||nativeM<=this.seaM+8)return out;
    const R=this.def.radius,c=this.cellM;
    const candidates=this.cells.get(this.key(Math.floor((dx*R+R)/c),Math.floor((dy*R+R)/c),Math.floor((dz*R+R)/c)));
    if(!candidates)return out;
    let sum=0,uncovered=1;
    for(const f of candidates) {
      const facing=dx*f.center[0]+dy*f.center[1]+dz*f.center[2];if(facing<=0)continue;
      const u=(dx*f.along[0]+dy*f.along[1]+dz*f.along[2])*R/facing;
      const s=u/f.halfLengthM;if(Math.abs(s)>=1)continue;
      const v=(dx*f.across[0]+dy*f.across[1]+dz*f.across[2])*R/facing;
      if(Math.abs(v)>4*f.widthM)continue;
      const knot=(s+1)*3,k=Math.min(5,Math.floor(knot)),t=knot-k,blend=smooth(t);
      const bend=f.bends[k]+(f.bends[k+1]-f.bends[k])*blend;
      const cross=(v-bend)/f.widthM,side=Math.abs(cross),end=smooth((1-Math.abs(s))/.24);
      const w=end*smooth((3.4-side)/.8)*smooth((nativeM-this.seaM-8)/40);
      if(w<=0)continue;
      const crest=cross<0?f.crests:f.secondaryCrests;
      const peak=crest[k]+(crest[k+1]-crest[k])*t;
      // The open floor widens between the shoulders. Flanks are broad angular
      // planes; outer skirts break into subordinate foothills, not more spikes.
      const floorWidth=.28+.28*(1-peak);
      const crestAt=1.02+.45*peak+(cross>0?.16:0);
      const valley=1-smooth((side-floorWidth)/.38);
      const rise=Math.max(0,Math.min(1,(side-floorWidth)/(crestAt-floorWidth)));
      const inner=rise<.4?rise*rise*.8:.128+(rise-.4)*(.872/.6);
      const outer=1-smooth((side-crestAt)/(3.4-crestAt));
      let mass=inner*outer*peak;
      let plateau=0,foothill=smooth((side-1.4)/.6)*outer;
      const floor=f.floorStartM+(f.floorEndM-f.floorStartM)*(s+1)*.5;
      let height:number;
      if(f.kind==='plateau'&&cross>0) {
        const profile=samplePlateauProfile(f,u,v-bend,this.plateauProfile);
        height=profile.heightM;plateau=profile.cap;mass=profile.mass;foothill=profile.apron;
      } else {
        const rock=planarField(u/420,v/360,(dx+dy+dz)*R/900,f.seed);
        const relief=f.reliefM*mass*(.88+.20*rock)*(.55+.45*end);
        height=floor+relief;
      }
      sum+=w;uncovered*=1-w;
      out.heightM+=height*w;
      out.mountain+=Math.min(1,mass*1.4)*w;
      out.valley+=valley*w;
      out.foothill+=foothill*w;
      out.plateau+=plateau*w;
    }
    if(sum>0) {
      out.weight=1-uncovered;out.heightM/=sum;out.mountain/=sum;
      out.valley/=sum;out.foothill/=sum;out.plateau/=sum;
    }
    return out;
  }
}
